import { useTranslation } from 'react-i18next';
import { Arbeitsbereich } from '../../entities';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  getHoursAndMinutesFromMinutes,
  getMonthNameByDateObject,
  getWeekdayByDate,
} from '../../../../utils';
import { Suspense, useState } from 'react';
import { useArbeitszeitUpdate } from '../../hooks/useArbeitszeitenUpdate';
import { handleOnCompleted } from './handleOnCompleted';
import {
  ArbeitsbereichSelect,
  ArbeitsbereichSelectLoading,
} from '../Comboboxen';
import { useEditSheetContext } from './EditSheetContext';
import { SheetClose, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { toast } from '@/hooks/use-toast';
import * as Sentry from '@sentry/react';
import { MitarbeiterAvatar } from '../Avatar/MitarbeiterAvatar';
import { faArrowLeft, faXmarkLarge } from '@fortawesome/pro-regular-svg-icons';
import { Button } from '@/components/ui/button';

// eslint-disable-next-line max-lines-per-function
export function Header() {
  const { t } = useTranslation();
  const { arbeitszeit } = useEditSheetContext();
  const arbeitszeitDate = arbeitszeit.datum;
  const date = `${
    getWeekdayByDate(arbeitszeitDate).shortage
  }., ${arbeitszeitDate.getDate()}. ${getMonthNameByDateObject(
    arbeitszeitDate,
  )}`;
  const [arbeitszeitUpdate] = useArbeitszeitUpdate();
  const [isUpdating, setIsUpdating] = useState(false);
  if (!arbeitszeit || !arbeitszeit.mitarbeiter) {
    return null;
  }

  const { hours, minutes } = arbeitszeit
    ? getHoursAndMinutesFromMinutes(arbeitszeit.dauer)
    : { hours: 0, minutes: 0 };

  async function onChangeArbeitsbereich(newArbeitsbereich: Arbeitsbereich) {
    setIsUpdating(true);
    arbeitszeitUpdate({
      arbeitszeitUpdateInput: {
        arbeitsbereichId: newArbeitsbereich.id,
      },
      arbeitszeitId: arbeitszeit.id,
    })
      .then((result) => {
        handleOnCompleted(result.data, t);
        setIsUpdating(false);
      })
      // Hier werden die Fehler abgefangen, die nicht von den Union Type gehandelt werden konnten
      .catch((e) => {
        toast({
          title: t(
            'domain.arbeitszeiten.sheet.header.arbeitszeitUpdateUnnownError',
          ),
        });
        Sentry.captureException(e);
        setIsUpdating(false);
      });
  }

  return (
    <SheetHeader className="bg-neutrals-N200">
      <SheetTitle>
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between px-4 pt-2">
            <div className="w-full flex justify-end gap-2 md:hidden">
              <SheetClose>
                <Button
                  variant="ghost"
                  className="text-neutrals-N800"
                  size="icon">
                  <FontAwesomeIcon icon={faXmarkLarge} />
                </Button>
              </SheetClose>
            </div>
            <SheetClose className="hidden md:flex md:items-center md:gap-2">
              <FontAwesomeIcon
                className="text-primary-blue-B500"
                icon={faArrowLeft}
              />
              <span className="text-sm text-neutrals-N800">
                {t('domain.arbeitszeiten.sheet.header.bearbeiten')}
              </span>
            </SheetClose>
            <span className="hidden md:inline-block md:text-sm md:text-neutrals-N800 md:text-right">
              {t('domain.arbeitszeiten.sheet.header.letzteAenderungAm', {
                date: arbeitszeit.geaendertAm.toLocaleString('de', {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                }),
              })}
              <br />
              <span>
                {t('domain.arbeitszeiten.sheet.header.letzteAenderungVon', {
                  user: arbeitszeit.geaendertVon,
                })}
              </span>
            </span>
          </div>
          <hr className="border-dashed border-1 border-neutrals-N400" />
          <div className="px-4 pb-2 flex gap-1 items-center md:items-stretch justify-between text-sm font-normal divide-x divide-neutrals-N400">
            <div className="flex items-center px-2 grow gap-2 max-w-52">
              <MitarbeiterAvatar
                vorname={arbeitszeit.mitarbeiter.vorname}
                nachname={arbeitszeit.mitarbeiter.nachname}
                bildUrl={arbeitszeit.mitarbeiter.bildUrl}
                extern={arbeitszeit.mitarbeiter.extern}
                className="bg-neutrals-N100"
              />
              <span className="truncate font-semibold">
                {`${arbeitszeit.mitarbeiter.vorname} ${arbeitszeit.mitarbeiter.nachname}`}
              </span>
            </div>
            <div className="hidden md:px-2 md:flex md:items-center md:grow">
              {date}
            </div>
            <div className="w-auto px-2 md:w-52">
              <Suspense
                fallback={
                  <ArbeitsbereichSelectLoading
                    name={arbeitszeit.arbeitsbereich.name}
                  />
                }>
                <ArbeitsbereichSelect
                  defaultArbeitsbereich={arbeitszeit.arbeitsbereich}
                  onChange={onChangeArbeitsbereich}
                  isUpdating={isUpdating}
                />
              </Suspense>
            </div>
            <div className="hidden md:flex md:items-center md:grow">
              <div className="flex gap-1 items-baseline grow px-2">
                <span className="font-semibold">{hours}</span>
                <span className=" text-neutrals-N700 text-xs">
                  {t('general.date.hours')}
                </span>
                <span className="font-semibold">{minutes}</span>
                <span className=" text-neutrals-N700 text-xs">
                  {t('general.date.minutes')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </SheetTitle>
    </SheetHeader>
  );
}
